import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface AppModule {
  id: string;
  moduleKey: string;
  name: string;
  icon: string;
  defaultRoute: string;
  isCore: boolean;
  displayOrder: number;
  isActive: boolean;
}

interface TenantMenuItem {
  mainMenuId: number;
  name: string;
  icon: string;
  route: string;
  defaultDisplayOrder: number;
  isActive: boolean;
  isEnabled: boolean;
  displayOrderOverride: number | null;
}

@Injectable({
  providedIn: 'root'
})
export class ModuleService {
  private readonly http = inject(HttpClient);
  private readonly apiBase = environment.apiBaseUrl;
  readonly menuChanged$ = new Subject<void>();

  getActiveModules(): Observable<AppModule[]> {
    return this.http.get<AppModule[]>(`${this.apiBase}/settings/modules`);
  }

  getAllModules(): Observable<AppModule[]> {
    return this.http.get<AppModule[]>(`${this.apiBase}/settings/modules/all`);
  }

  getMyMenu(): Observable<AppModule[]> {
    return this.http.get<unknown>(`${this.apiBase}/menu/me`).pipe(
      map(response => this.normalizeMenuResponse(response))
    );
  }

  getTenantMenuConfiguration(): Observable<AppModule[]> {
    return this.http.get<unknown>(`${this.apiBase}/menu/admin/tenant`).pipe(
      map(response => {
        const root = this.isRecord(response) ? response : null;
        const data = root && this.isRecord(root['data']) ? root['data'] : root;
        const items = data?.['items'];
        if (!Array.isArray(items)) return [];

        return items
          .filter((item): item is TenantMenuItem => this.isRecord(item) && Number.isFinite(Number(item['mainMenuId'])))
          .map(item => ({
            id: String(item.mainMenuId),
            moduleKey: String(item.mainMenuId),
            name: String(item.name ?? ''),
            icon: this.primeIcon(item.icon),
            defaultRoute: String(item.route ?? ''),
            isCore: false,
            displayOrder: Number(item.displayOrderOverride ?? item.defaultDisplayOrder ?? 0),
            isActive: item.isActive === true && item.isEnabled === true,
          }))
          .sort((a, b) => a.displayOrder - b.displayOrder);
      })
    );
  }

  toggleModuleStatus(mainMenuId: number, isEnabled: boolean): Observable<unknown> {
    return this.http.put<unknown>(`${this.apiBase}/menu/admin/tenant`, {
      items: [
        {
          mainMenuId,
          isEnabled,
          displayOrderOverride: null,
        },
      ],
    }).pipe(tap(() => this.menuChanged$.next()));
  }


  private normalizeMenuResponse(response: unknown): AppModule[] {
    const record = this.isRecord(response) ? response : null;
    const rawSource = Array.isArray(response)
      ? response
      : record
        ? record['data'] ?? record['items'] ?? record['menu'] ?? record['modules']
        : [];
    const source = this.isRecord(rawSource)
      ? rawSource['items'] ?? rawSource['menu'] ?? rawSource['modules']
      : rawSource;

    if (!Array.isArray(source)) return [];

    const modules: AppModule[] = [];
    const visit = (item: unknown, fallbackOrder: number): void => {
      if (!this.isRecord(item)) return;
      const key = String(item['moduleKey'] ?? item['key'] ?? item['code'] ?? item['mainMenuId'] ?? item['id'] ?? '').trim();
      const name = String(item['name'] ?? item['label'] ?? item['title'] ?? key).trim();
      if (key && name) {
        modules.push({
          id: String(item['mainMenuId'] ?? item['id'] ?? key),
          moduleKey: key,
          name,
          icon: this.primeIcon(item['icon']),
          defaultRoute: String(item['defaultRoute'] ?? item['route'] ?? item['url'] ?? ''),
          isCore: Boolean(item['isCore']),
          displayOrder: Number(item['displayOrderOverride'] ?? item['displayOrder'] ?? item['defaultDisplayOrder'] ?? item['order'] ?? fallbackOrder),
          isActive: item['isActive'] !== false && item['isEnabled'] !== false,
        });
      }

      const children = item['children'] ?? item['items'];
      if (Array.isArray(children)) children.forEach((child, index) => visit(child, fallbackOrder + index + 1));
    };

    source.forEach((item, index) => visit(item, index));
    return modules;
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  private primeIcon(icon: unknown): string {
    const value = String(icon ?? '').trim();
    if (!value) return 'pi pi-circle';
    const name = value.replace(/^pi\s+pi-/, '').replace(/^pi-/, '');
    const aliases: Record<string, string> = {
      settings: 'cog',
      'file-clock': 'clock',
    };
    return `pi pi-${aliases[name] ?? name}`;
  }
}
